/**
 * Every Pick'em setting as one set of fields (ISSUE-1303, ISSUE-1306). The
 * create step and the Settings dialog both render this, so a Commissioner
 * sees the same words in the same order in both places.
 *
 * Server resolves, client renders: whether the league has spreads, which
 * fields are frozen and any field errors all come in as props from
 * `usePickemSetupPreview` or `usePickemPoolGet`. This file only edits the
 * draft.
 */
import { Input, Radio, Select } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import {
  PICKEM_BASIS_LABEL,
  PICKEM_BASIS_MONEYLINE_LABEL,
  PICKEM_BASIS_NO_SPREADS_DESCRIPTION,
  PICKEM_BASIS_SPREAD_DESCRIPTION,
  PICKEM_BASIS_SPREAD_LABEL,
  PICKEM_END_DATE_LABEL,
  PICKEM_END_MODE_DATE_LABEL,
  PICKEM_END_MODE_LABEL,
  PICKEM_END_MODE_TBD_DESCRIPTION,
  PICKEM_END_MODE_TBD_LABEL,
  PICKEM_FROZEN_NOTE,
  PICKEM_GROUPING_DAY_LABEL,
  PICKEM_GROUPING_LABEL,
  PICKEM_GROUPING_WEEK_LABEL,
  PICKEM_LOSS_POINTS_HINT,
  PICKEM_LOSS_POINTS_LABEL,
  PICKEM_NUMBER_REQUIRED,
  PICKEM_PICKS_COUNT_LABEL,
  PICKEM_PICKS_MODE_ALL_LABEL,
  PICKEM_PICKS_MODE_COUNT_LABEL,
  PICKEM_PICKS_MODE_LABEL,
  PICKEM_SCORING_CONFIDENCE_DESCRIPTION,
  PICKEM_SCORING_CONFIDENCE_LABEL,
  PICKEM_SCORING_LABEL,
  PICKEM_SCORING_STANDARD_DESCRIPTION,
  PICKEM_SCORING_STANDARD_LABEL,
  PICKEM_START_DATE_LABEL,
  PICKEM_TIE_LABEL,
  PICKEM_TIE_LOSS_LABEL,
  PICKEM_TIE_VOID_DESCRIPTION,
  PICKEM_TIE_VOID_LABEL,
  PICKEM_TIE_WIN_LABEL,
  PICKEM_TIEBREAKER_LABEL,
  PICKEM_TIEBREAKER_NONE_DESCRIPTION,
  PICKEM_TIEBREAKER_NONE_LABEL,
  PICKEM_TIEBREAKER_TOTAL_DESCRIPTION,
  PICKEM_TIEBREAKER_TOTAL_LABEL,
  PICKEM_WEEK_DAYS,
  PICKEM_WEEK_START_LABEL,
  PICKEM_WIN_POINTS_LABEL,
} from "./pickemCopy";
import type { typePickemSetupDraft } from "./pickemCreateSetupDraft";
import type { typePickemFieldError } from "./pickemTypes";

const PICK_COUNT_ITEMS = Array.from({ length: 10 }, (_, index) => ({
  value: String(index + 1),
  label: String(index + 1),
}));

/** The plain sentence under a field the server turned down. */
export function pickemFieldErrorMessage(field: string): string {
  switch (field) {
    case "picksRequiredCount":
      return "Choose from 1 to 10 games.";
    case "winPoints":
    case "lossPoints":
      return "Enter a whole number of 0 or more.";
    case "startDate":
      return "Choose a start date inside this league's season.";
    case "endDate":
      return "The end date has to come after the start date.";
    case "pickBasis":
      return "This league has no spreads. Choose Pick the winner.";
    case "weekStartDay":
      return "Choose the day the week starts.";
    default:
      return "This setting needs another look.";
  }
}

function errorFor(
  errors: readonly typePickemFieldError[],
  field: string,
): string | undefined {
  return errors.some((error) => error.field === field)
    ? pickemFieldErrorMessage(field)
    : undefined;
}

const PointsInput = (props: {
  label: string;
  hint?: string | undefined;
  value: number | null;
  error: string | undefined;
  disabled: boolean;
  onChangeValue: (value: number | null) => void;
  testId: string;
}): ReactNode => {
  const [blankError, setBlankError] = useState<string | null>(null);
  return (
    <Input
      label={props.label}
      hint={props.hint}
      type="number"
      inputMode="numeric"
      min={0}
      value={props.value === null ? "" : String(props.value)}
      disabled={props.disabled}
      error={blankError ?? props.error}
      onChange={(event) => {
        const raw = event.target.value.trim();
        if (raw === "") {
          props.onChangeValue(null);
          return;
        }
        if (!/^\d{1,3}$/.test(raw)) return;
        setBlankError(null);
        props.onChangeValue(Number(raw));
      }}
      onBlur={() => {
        setBlankError(props.value === null ? PICKEM_NUMBER_REQUIRED : null);
      }}
      data-testid={props.testId}
    />
  );
};

export type typePickemSettingsFieldsProps = {
  draft: typePickemSetupDraft;
  onChange: (next: typePickemSetupDraft) => void;
  /** From the server: false turns Beat the spread off for this league. */
  hasSpreads: boolean;
  /** Field names the server says can no longer change. */
  frozenFields?: readonly string[] | undefined;
  fieldErrors?: readonly typePickemFieldError[] | undefined;
};

export const PickemSettingsFields = (
  props: typePickemSettingsFieldsProps,
): ReactNode => {
  const { draft, onChange } = props;
  const frozen = new Set(props.frozenFields ?? []);
  const errors = props.fieldErrors ?? [];
  const hasFrozen = frozen.size > 0;

  return (
    <div className="flex flex-col gap-6" data-testid="pickem-settings-fields">
      <section className="flex flex-col gap-4">
        <div data-testid="pickem-field-scoring">
          <Radio
            label={PICKEM_SCORING_LABEL}
            items={[
              {
                value: "standard",
                label: PICKEM_SCORING_STANDARD_LABEL,
                description: PICKEM_SCORING_STANDARD_DESCRIPTION,
              },
              {
                value: "confidence",
                label: PICKEM_SCORING_CONFIDENCE_LABEL,
                description: PICKEM_SCORING_CONFIDENCE_DESCRIPTION,
              },
            ]}
            value={draft.scoringFormat ?? undefined}
            disabled={frozen.has("scoringFormat")}
            error={errorFor(errors, "scoringFormat")}
            onValueChange={(next) => {
              onChange({
                ...draft,
                scoringFormat:
                  next === "confidence" ? "confidence" : "standard",
              });
            }}
          />
        </div>
        {draft.scoringFormat === "standard" ? (
          <div className="flex flex-col gap-4">
            <PointsInput
              label={PICKEM_WIN_POINTS_LABEL}
              value={draft.winPoints}
              error={errorFor(errors, "winPoints")}
              disabled={frozen.has("winPoints")}
              onChangeValue={(winPoints) => {
                onChange({ ...draft, winPoints });
              }}
              testId="pickem-field-win-points"
            />
            <PointsInput
              label={PICKEM_LOSS_POINTS_LABEL}
              hint={PICKEM_LOSS_POINTS_HINT}
              value={draft.lossPoints}
              error={errorFor(errors, "lossPoints")}
              disabled={frozen.has("lossPoints")}
              onChangeValue={(lossPoints) => {
                onChange({ ...draft, lossPoints });
              }}
              testId="pickem-field-loss-points"
            />
          </div>
        ) : null}
        <div data-testid="pickem-field-basis">
          <Radio
            label={PICKEM_BASIS_LABEL}
            items={[
              { value: "moneyline", label: PICKEM_BASIS_MONEYLINE_LABEL },
              {
                value: "spread",
                label: PICKEM_BASIS_SPREAD_LABEL,
                description: props.hasSpreads
                  ? PICKEM_BASIS_SPREAD_DESCRIPTION
                  : PICKEM_BASIS_NO_SPREADS_DESCRIPTION,
                disabled: !props.hasSpreads,
              },
            ]}
            value={draft.pickBasis ?? undefined}
            disabled={frozen.has("pickBasis")}
            error={errorFor(errors, "pickBasis")}
            onValueChange={(next) => {
              onChange({
                ...draft,
                pickBasis: next === "spread" ? "spread" : "moneyline",
              });
            }}
          />
        </div>
        {hasFrozen ? (
          <p
            className="text-caption text-ink-secondary"
            data-testid="pickem-frozen-note"
          >
            {PICKEM_FROZEN_NOTE}
          </p>
        ) : null}
      </section>

      <section className="flex flex-col gap-4">
        <div data-testid="pickem-field-picks-mode">
          <Radio
            label={PICKEM_PICKS_MODE_LABEL}
            orientation="horizontal"
            items={[
              { value: "all", label: PICKEM_PICKS_MODE_ALL_LABEL },
              { value: "count", label: PICKEM_PICKS_MODE_COUNT_LABEL },
            ]}
            value={draft.picksRequiredMode ?? undefined}
            disabled={frozen.has("picksRequiredMode")}
            onValueChange={(next) => {
              onChange({
                ...draft,
                picksRequiredMode: next === "count" ? "count" : "all",
              });
            }}
          />
        </div>
        {draft.picksRequiredMode === "count" ? (
          <div data-testid="pickem-field-picks-count">
            <Select
              label={PICKEM_PICKS_COUNT_LABEL}
              items={PICK_COUNT_ITEMS}
              value={
                draft.picksRequiredCount === null
                  ? undefined
                  : String(draft.picksRequiredCount)
              }
              disabled={frozen.has("picksRequiredCount")}
              error={errorFor(errors, "picksRequiredCount")}
              onValueChange={(next) => {
                onChange({ ...draft, picksRequiredCount: Number(next) });
              }}
            />
          </div>
        ) : null}
        <div data-testid="pickem-field-tiebreaker">
          <Radio
            label={PICKEM_TIEBREAKER_LABEL}
            items={[
              {
                value: "none",
                label: PICKEM_TIEBREAKER_NONE_LABEL,
                description: PICKEM_TIEBREAKER_NONE_DESCRIPTION,
              },
              {
                value: "totalPoints",
                label: PICKEM_TIEBREAKER_TOTAL_LABEL,
                description: PICKEM_TIEBREAKER_TOTAL_DESCRIPTION,
              },
            ]}
            value={draft.tiebreaker ?? undefined}
            disabled={frozen.has("tiebreaker")}
            onValueChange={(next) => {
              onChange({
                ...draft,
                tiebreaker: next === "totalPoints" ? "totalPoints" : "none",
              });
            }}
          />
        </div>
        <div data-testid="pickem-field-tie-policy">
          <Radio
            label={PICKEM_TIE_LABEL}
            items={[
              {
                value: "tieIsVoid",
                label: PICKEM_TIE_VOID_LABEL,
                description: PICKEM_TIE_VOID_DESCRIPTION,
              },
              { value: "tieIsWin", label: PICKEM_TIE_WIN_LABEL },
              { value: "tieIsLoss", label: PICKEM_TIE_LOSS_LABEL },
            ]}
            value={draft.tiePolicy ?? undefined}
            disabled={frozen.has("tiePolicy")}
            onValueChange={(next) => {
              onChange({
                ...draft,
                tiePolicy:
                  next === "tieIsWin"
                    ? "tieIsWin"
                    : next === "tieIsLoss"
                      ? "tieIsLoss"
                      : "tieIsVoid",
              });
            }}
          />
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <div data-testid="pickem-field-grouping">
          <Radio
            label={PICKEM_GROUPING_LABEL}
            orientation="horizontal"
            items={[
              { value: "week", label: PICKEM_GROUPING_WEEK_LABEL },
              { value: "day", label: PICKEM_GROUPING_DAY_LABEL },
            ]}
            value={draft.roundGrouping ?? undefined}
            disabled={frozen.has("roundGrouping")}
            onValueChange={(next) => {
              onChange({
                ...draft,
                roundGrouping: next === "day" ? "day" : "week",
                weekStartDay:
                  next === "week" && draft.weekStartDay === null
                    ? 1
                    : draft.weekStartDay,
              });
            }}
          />
        </div>
        {draft.roundGrouping === "week" ? (
          <Select
            label={PICKEM_WEEK_START_LABEL}
            items={[...PICKEM_WEEK_DAYS]}
            value={
              draft.weekStartDay === null
                ? undefined
                : String(draft.weekStartDay)
            }
            disabled={frozen.has("weekStartDay")}
            error={errorFor(errors, "weekStartDay")}
            onValueChange={(next) => {
              onChange({ ...draft, weekStartDay: Number(next) });
            }}
          />
        ) : null}
        <Input
          label={PICKEM_START_DATE_LABEL}
          type="date"
          value={draft.startDate ?? ""}
          disabled={frozen.has("startDate")}
          error={errorFor(errors, "startDate")}
          onChange={(event) => {
            const value = event.target.value;
            onChange({ ...draft, startDate: value === "" ? null : value });
          }}
          data-testid="pickem-field-start-date"
        />
        <div data-testid="pickem-field-end-mode">
          <Radio
            label={PICKEM_END_MODE_LABEL}
            items={[
              {
                value: "tbd",
                label: PICKEM_END_MODE_TBD_LABEL,
                description: PICKEM_END_MODE_TBD_DESCRIPTION,
              },
              { value: "date", label: PICKEM_END_MODE_DATE_LABEL },
            ]}
            value={draft.endDateMode}
            disabled={frozen.has("endDateMode")}
            onValueChange={(next) => {
              onChange({
                ...draft,
                endDateMode: next === "date" ? "date" : "tbd",
              });
            }}
          />
        </div>
        {draft.endDateMode === "date" ? (
          <Input
            label={PICKEM_END_DATE_LABEL}
            type="date"
            value={draft.endDate ?? ""}
            disabled={frozen.has("endDate")}
            error={errorFor(errors, "endDate")}
            onChange={(event) => {
              const value = event.target.value;
              onChange({ ...draft, endDate: value === "" ? null : value });
            }}
            data-testid="pickem-field-end-date"
          />
        ) : null}
      </section>
    </div>
  );
};
